import React, { Fragment, Suspense, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

import { AdminProvider } from "./contexts/AdminContext";
import routes, { renderRoutes } from "./routes";
import ScrollToTop from "./utils/ScrollToTop";
import SplashScreen from "./components/SplashScreen";

import { getAgencySettings, permittedRoute } from "./utils/common";
import useAuth from "./hooks/useAuth";
import { environment, siteURLs } from "./utils/constant";
import ConnectGoHighLevelCode from "./components/connectGoHighLevel/ConnectGoHighLevelCode";
import { clearEmailVerificationFlowStorage } from "./utils/emailVerificationFlow";

const HOME_CHATBOT_SCRIPT_ID = "bottbuddy-home-chatbot-script";
const HOME_CHATBOT_CONTAINER_ID = "chatbot-container";
const HOME_CHATBOT_ID_PROD = "298595683948";
const HOME_CHATBOT_ID_DEV = "551455280129";

const removeHomeChatbot = () => {
  document.getElementById(HOME_CHATBOT_SCRIPT_ID)?.remove();
  document.getElementById(HOME_CHATBOT_CONTAINER_ID)?.remove();
};

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const parentAgency = searchParams.get("agency") || null;
  const referral = searchParams.get("referral") || null;

  const { user, accessType, updateCompanyInfo } = useAuth();

  /* ---------------------------------- Utils --------------------------------- */

  const handleGetAgencySettings = async () => {
    const agency = localStorage.getItem("parentUserRefferId") || parentAgency;

    const res = await getAgencySettings(agency);

    const companyInfo = {
      agency,
      orgName: res?.res?.data?.data?.organizationName,
      logo: res?.res?.data?.data?.logoLink,
    };

    updateCompanyInfo(companyInfo);

    localStorage.setItem("company_info", JSON.stringify(companyInfo));
    localStorage.setItem("agency", agency);
    localStorage.setItem("isAuth", false);
    localStorage.removeItem("referral");
  };

  /* ------------------------------ Geo Location ------------------------------- */

  // useEffect(() => {
  //   fetch("https://ipapi.co/json/")
  //     .then((res) => res.json())
  //     .then((data) => {
  //       localStorage.setItem("country", data.country_name);
  //       localStorage.setItem("currency", data.currency);
  //     })
  //     .catch(() => {
  //       localStorage.setItem("country", "India");
  //       localStorage.setItem("currency", "INR");
  //     });
  // }, []);

  /* --------------------------- Initial URL Handling --------------------------- */

  useEffect(() => {
    if (
      parentAgency &&
      (!localStorage.getItem("agency") ||
        localStorage.getItem("agency") !== parentAgency)
    ) {
      handleGetAgencySettings();
    } else if (!parentAgency) {
      localStorage.removeItem("agency");
      localStorage.removeItem("logo");
      localStorage.removeItem("orgName");
      localStorage.removeItem("isAuth");
    }

    if (localStorage.getItem("company_info")) {
      updateCompanyInfo(JSON.parse(localStorage.getItem("company_info")));
    }

    if (
      referral &&
      (!localStorage.getItem("referral") ||
        localStorage.getItem("referral") !== referral)
    ) {
      localStorage.setItem("referral", referral);
      localStorage.removeItem("agency");
      localStorage.removeItem("logo");
      localStorage.removeItem("orgName");
      localStorage.removeItem("isAuth");
    } else if (!referral) {
      localStorage.removeItem("referral");
    }

    localStorage.setItem("envPath", window.location.href);
  }, []);

  /* ----------------------------- Auth Navigation ------------------------------ */

  useEffect(() => {
    if (!user) {
      if (
        localStorage.getItem("email-verify-expiration") &&
        new Date().getTime() > localStorage.getItem("email-verify-expiration")
      ) {
        clearEmailVerificationFlowStorage({ clearPendingPlan: true });
        localStorage.removeItem("accessToken");
        navigate("/login");
      }

      if (parentAgency || localStorage.getItem("agency")) {
        const agencyParam = localStorage.getItem("agency") || parentAgency;

        if (localStorage.getItem("isAuth") === "false") {
          navigate(`${location.pathname}?agency=${agencyParam}`, {
            replace: true,
          });
        }
      }

      if (referral || localStorage.getItem("referral")) {
        const referralParam = localStorage.getItem("referral") || referral;

        navigate(`${location.pathname}?referral=${referralParam}`, {
          replace: true,
        });
      }
    } else if (user && location.pathname) {
      if (user.isEmailVerified) {
        const newPath =
          location.pathname === "/ghl/connect"
            ? "/ghl/connect"
            : permittedRoute(location.pathname, accessType, user);

        const params = new URLSearchParams(location.search);

        if (localStorage.getItem("parentUserRefferId")) {
          params.set("agency", localStorage.getItem("parentUserRefferId"));
        }

        navigate(
          {
            pathname: newPath,
            search: params?.toString(),
          },
          { replace: true },
        );
      }
    }
  }, [location.pathname]);

  /* ---------------------------- Home Chat Widget ----------------------------- */

  useEffect(() => {
    const existingScript = document.getElementById(HOME_CHATBOT_SCRIPT_ID);

    if (location.pathname !== "/") {
      removeHomeChatbot();
      return;
    }

    const chatbotBaseUrl =
      process.env.REACT_APP_URL_ENVIRONMENT === "production"
        ? "https://bottbuddy.com"
        : "https://test-dev.bottbuddy.com";

    const chatbotCode =
      process.env.REACT_APP_URL_ENVIRONMENT === "production"
        ? HOME_CHATBOT_ID_PROD
        : HOME_CHATBOT_ID_DEV;

    const chatbotScriptSrc = `${chatbotBaseUrl}/embed/chatbot.js?id=${chatbotCode}`;

    if (existingScript?.getAttribute("src") === chatbotScriptSrc) {
      return;
    }

    existingScript?.remove();

    const script = document.createElement("script");
    script.id = HOME_CHATBOT_SCRIPT_ID;
    script.src = chatbotScriptSrc;
    script.async = true;
    script.setAttribute("allowfullscreen", "");
    script.onload = () => {
      if (!document.getElementById(HOME_CHATBOT_CONTAINER_ID)) {
        document.dispatchEvent(new Event("DOMContentLoaded"));
      }
    };

    document.body.appendChild(script);

    return () => {
      removeHomeChatbot();
    };
  }, [location.pathname]);

  // useEffect(() => {
  //   // Google Tag Manager
  //   // (function (w, d, s, l, i) {
  //   //   w[l] = w[l] || [];
  //   //   w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  //   //   var f = d.getElementsByTagName(s)[0],
  //   //     j = d.createElement(s),
  //   //     dl = l !== "dataLayer" ? "&l=" + l : "";
  //   //   j.async = true;
  //   //   j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
  //   //   f.parentNode.insertBefore(j, f);
  //   // })(window, document, "script", "dataLayer", "GTM-TLRTCRWT");

  //   // const script = document.createElement("script");
  //   // script.src = "https://www.googletagmanager.com/gtag/js?id=G-BSCHGD878P";
  //   // script.async = true;
  //   // document.head.appendChild(script);

  //   // script.onload = () => {
  //   //   window.dataLayer = window.dataLayer || [];
  //   //   function gtag() {
  //   //     window.dataLayer.push(arguments);
  //   //   }

  //   //   gtag("js", new Date());

  //   //   // GA4
  //   //   gtag("config", "G-BSCHGD878P");

  //   //   // Google Ads
  //   //   console.log("test changes");
  //   //   gtag("config", "AW-17856034610");
  //   // };

  //   // // Google Ads tag
  //   // const gadsScript = document.createElement("script");
  //   // gadsScript.src =
  //   //   "https://www.googletagmanager.com/gtag/js?id=AW-17856034610";
  //   // gadsScript.async = true;
  //   // document.head.appendChild(gadsScript);

  //   // gadsScript.onload = () => {
  //   //   window.dataLayer = window.dataLayer || [];
  //   //   function gtag() {
  //   //     window.dataLayer.push(arguments);
  //   //   }
  //   //   gtag("js", new Date());
  //   //   gtag("config", "AW-17856034610");
  //   // };

  //   // // Google Analytics tag
  //   // const gaScript = document.createElement("script");
  //   // gaScript.src = "https://www.googletagmanager.com/gtag/js?id=G-BSCHGD878P";
  //   // gaScript.async = true;
  //   // document.head.appendChild(gaScript);

  //   // gaScript.onload = () => {
  //   //   window.dataLayer = window.dataLayer || [];
  //   //   function gtag() {
  //   //     window.dataLayer.push(arguments);
  //   //   }
  //   //   gtag("js", new Date());
  //   //   gtag("config", "G-BSCHGD878P");
  //   // };
  // }, []);

  useEffect(() => {
    // Google Tag Manager
    (function (w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l !== "dataLayer" ? "&l=" + l : "";
      j.async = true;
      j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, "script", "dataLayer", "GTM-TLRTCRWT");

    const script = document.createElement("script");
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-BSCHGD878P";
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }

      // --- ADD THIS LINE ---
      window.gtag = window.gtag || gtag;
      // --------------------

      gtag("js", new Date());

      // GA4
      gtag("config", "G-BSCHGD878P");

      // Google Ads
      console.log("test changes");
      gtag("config", "AW-17856034610");
    };
  }, []);

  /* --------------------------------- Render ---------------------------------- */

  console.log(siteURLs[environment()], "siteURLs[environment()]");
  return (
    <Fragment>
      <GoogleReCaptchaProvider
        reCaptchaKey="6LfSoC8sAAAAAFtwnbDpFj437N7AFd8u2cZZGlmG"
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "head",
        }}
      >
        <Suspense fallback={<SplashScreen />}>
          <AdminProvider>
            <ScrollToTop />
            <ConnectGoHighLevelCode />
            {renderRoutes(routes)}
          </AdminProvider>
        </Suspense>
      </GoogleReCaptchaProvider>
    </Fragment>
  );
};

export default App;

// import { AdminProvider } from "./contexts/AdminContext";
// import routes, { renderRoutes } from "./routes";
// import ScrollToTop from "./utils/ScrollToTop";
// import SplashScreen from "./components/SplashScreen";
// import React, { Suspense, useEffect } from "react";
// // import { getToken, onMessage } from "firebase/messaging";
// // import { messaging } from "./firebase/firebaseConfig";

// import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
// import { getAgencySettings, permittedRoute } from "./utils/common";
// import useAuth from "./hooks/useAuth";
// import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

// const App = () => {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const [searchParams] = useSearchParams();
//   const parentAgency = searchParams.get("agency") || null;
//   const referral = searchParams.get("referral") || null;
//   const { user, accessType, updateCompanyInfo } = useAuth();

//   function waitForServiceWorker() {
//     return new Promise((resolve, reject) => {
//       if (navigator.serviceWorker.controller) {
//         console.log("Service worker is already available");
//         resolve();
//       } else {
//         navigator.serviceWorker.oncontrollerchange = () => {
//           console.log("Service worker errir");
//           resolve();
//         };
//       }
//     });
//   }

//   // async function requestNotificationPermission() {
//   //   console.log("Current Notification.permission:", Notification.permission);

//   //   if (Notification.permission === 'default') {
//   //     const permission = await Notification.requestPermission();
//   //     console.log("Permission after request:", permission);

//   //     if (permission === 'granted') {
//   //       console.log("Notification permission granted.");
//   //       await waitForServiceWorker();
//   //       await getMessagingToken();
//   //     } else {
//   //       console.error("Notification permission denied.");
//   //     }
//   //   } else if (Notification.permission === 'granted') {
//   //     console.log("Notification permission already granted.");
//   //     await waitForServiceWorker();
//   //     await getMessagingToken();
//   //   } else if (Notification.permission === 'denied') {
//   //     console.error("Permission was blocked. Please enable notifications in your browser settings.");
//   //   }
//   // }

//   // async function getMessagingToken() {
//   //   const token = await getToken(messaging, {
//   //     vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
//   //   });
//   //   console.log("Token generated : ", token);
//   //   localStorage.setItem("FcmToken", token)
//   // }

//   useEffect(() => {
//     fetch("https://ipapi.co/json/")
//       .then((res) => res.json())
//       .then((data) => {
//         console.log("Country:", data.country_name);
//         localStorage.setItem("country", data.country_name);
//         localStorage.setItem("currency", data.currency);
//       })
//       .catch((err) => {
//         console.error("Failed to fetch location", err);
//         localStorage.setItem("country", "India");
//         localStorage.setItem("currency", "INR");
//       });
//   }, []);

//   useEffect(() => {
//     if (
//       !!parentAgency &&
//       (!localStorage.getItem("agency") ||
//         localStorage.getItem("agency") != parentAgency)
//     ) {
//       handleGetAgencySettings();
//     } else if (!parentAgency) {
//       localStorage.removeItem("agency");
//       localStorage.removeItem("logo");
//       localStorage.removeItem("orgName");
//       localStorage.removeItem("isAuth");
//     }

//     if (!!localStorage.getItem("company_info")) {
//       updateCompanyInfo(JSON.parse(localStorage.getItem("company_info")));
//     }

//     if (
//       !!referral &&
//       (!localStorage.getItem("referral") ||
//         localStorage.getItem("referral") != referral)
//     ) {
//       localStorage.setItem("referral", referral);
//       localStorage.removeItem("agency");
//       localStorage.removeItem("logo");
//       localStorage.removeItem("orgName");
//       localStorage.removeItem("isAuth");
//     } else if (!referral) {
//       localStorage.removeItem("referral");
//     }

//     // requestNotificationPermission();
//     // onMessage(messaging, (payload) => {
//     //   toast(<MessageNotification notification={payload.notification} />, { theme: "dark" });
//     // });

//     localStorage.setItem("envPath", window.location.href);

//     // window.addEventListener('storage', handleGetAgencySettings);

//     // return () => {
//     //   // Cleanup the event listener on component unmount
//     //   window.removeEventListener('storage', handleGetAgencySettings);
//     // };
//   }, []);

//   const handleGetAgencySettings = async () => {
//     let res = await getAgencySettings(
//       localStorage.getItem("parentUserRefferId") || parentAgency
//     );
//     updateCompanyInfo({
//       agency: localStorage.getItem("parentUserRefferId") || parentAgency,
//       orgName: res?.res?.data?.data?.organizationName,
//       logo: res?.res?.data?.data?.logoLink,
//     });
//     localStorage.setItem(
//       "company_info",
//       JSON.stringify({
//         agency: localStorage.getItem("parentUserRefferId") || parentAgency,
//         orgName: res?.res?.data?.data?.organizationName,
//         logo: res?.res?.data?.data?.logoLink,
//       })
//     );
//     localStorage.setItem(
//       "agency",
//       localStorage.getItem("parentUserRefferId") || parentAgency
//     );
//     localStorage.setItem("isAuth", false);
//     localStorage.removeItem("referral");
//   };

//   useEffect(() => {
//     if (!user) {
//       if (
//         !!localStorage.getItem("email-verify-expiration") &&
//         new Date().getTime() > localStorage.getItem("email-verify-expiration")
//       ) {
//         localStorage.removeItem("email-verify-expiration");
//         localStorage.removeItem("action-type");
//         localStorage.removeItem("email-verify");
//         localStorage.removeItem("accessToken");
//         navigate("/login");
//       }
//       if (!!parentAgency || !!localStorage.getItem("agency")) {
//         console.log("current path", location.pathname);
//         let agencyParam = localStorage.getItem("agency") || parentAgency;
//         if (localStorage.getItem("isAuth") === "false") {
//           navigate(location.pathname + "?agency=" + agencyParam, {
//             replace: true,
//           });
//         }
//       }
//       if (!!referral || !!localStorage.getItem("referral")) {
//         let referralParam = localStorage.getItem("referral") || referral;
//         navigate(location.pathname + "?referral=" + referralParam, {
//           replace: true,
//         });
//         // else {
//         //   navigate(location.pathname + location.search + "&referral=" + referralParam, { replace: true })
//         // }
//       }
//     } else if (!!location.pathname && !!user) {
//       console.log(user.isEmailVerified, "user.isEmailVerified");
//       if (user.isEmailVerified) {
//         let newPath =
//           location.pathname == "/ghl/connect"
//             ? "/ghl/connect"
//             : permittedRoute(location.pathname, accessType, user);
//         const params = new URLSearchParams(location.search);
//         if (localStorage.getItem("parentUserRefferId")) {
//           params.set("agency", localStorage.getItem("parentUserRefferId"));
//         }
//         navigate(
//           {
//             pathname: newPath,
//             search: params.toString(), // New query string with appended params
//           },
//           { replace: true }
//         );
//       }

//       // navigate(location.search
//       //   ? permittedRoute(location.pathname, accessType, user) + location.search + "&agency=" + localStorage.getItem("parentUserRefferId")
//       //   : permittedRoute(location.pathname, accessType, user) + "?agency=" + localStorage.getItem("parentUserRefferId"), { replace: true })
//       // + location.searchParams + "?agency=" + localStorage.getItem("parentUserRefferId"), { replace: true })
//     }
//     // else {
//     //   navigate(location.search ? permittedRoute(location.pathname, accessType, user) + location.search : permittedRoute(location.pathname, accessType, user), { replace: true })
//     // }
//   }, [location.pathname]);

//   // useEffect(() => {

//   // }, [location.pathname])
//   console.log("console log to check pipeline 28 Oct 14:07");

//   return (
//     <React.Fragment>
//       <GoogleReCaptchaProvider
//         reCaptchaKey={`6LfSoC8sAAAAAFtwnbDpFj437N7AFd8u2cZZGlmG`}
//         scriptProps={{
//           async: true,
//           defer: true,
//           appendTo: "head",
//         }}
//       >
//         <Suspense fallback={<SplashScreen />}>
//           <AdminProvider>
//             {/* <NetworkStatus/> */}
//             <ScrollToTop />
//             {renderRoutes(routes)}
//           </AdminProvider>
//         </Suspense>
//       </GoogleReCaptchaProvider>
//     </React.Fragment>
//   );
// };

// export default App;
